import config from '@/common/config';
import moment from 'moment';

export class IntercomManager {
    constructor() {
        this.storeGetters = null;
        this.client = IntercomManager.getClient();
        this.apiKey = config.VUE_APP_INTERCOM_ID;
        this.isTrackingActive = this.apiKey !== false;
        this.isBooted = false;
        this.lastIdentified = null;
    }

    /**
     * Detects the Intercom client in the browser and returns it. Returns false if missing.
     * @returns {Function|boolean}
     */
    static getClient() {
        const noClient = () => null;
        try {
            if (window && window.Intercom) {
                return (...params) => window.Intercom(...params);
            }
            return noClient;
        } catch (error) {
            return noClient;
        }
    }

    /**
     * Base parameters that are shared by both guests and authenticated users in Intercom.
     * @returns {{app_id: string}}
     */
    getBaseParams() {
        return {
            app_id: this.apiKey,
        };
    }

    initialize(getters) {
        if (getters['user/isImpersonating']) {
            // disable tracking altogether on impersonation
            this.isTrackingActive = false;
        }
        if (this.isTrackingActive) {
            this.storeGetters = getters;
            if (this.storeGetters['user/isGuest']) {
                this.identifyGuest();
            } else {
                this.identifyUser();
            }
        }
    }

    getUserParams() {
        const {user, userHash} = this.storeGetters['user/decodedToken'];
        const organizationId = this.storeGetters['user/organizationId'];
        const membership = this.storeGetters['user/memberships']
            .find(({organization: {id}}) => id === organizationId);

        if ((this.lastIdentified !== null && this.lastIdentified === userHash) || !membership) {
            return null;
        }
        this.lastIdentified = userHash;

        return {
            ...this.getBaseParams(),
            email: user.email,
            name: user.name,
            created_at: moment(user.createdTime)
                .format('X'),
            user_hash: userHash,
            company: {
                id: membership.organization.id,
                name: membership.organization.name,
                created_at: moment(membership.organization.createdTime)
                    .format('X'),
            },
        };
    }

    identifyGuest() {
        this.bootClient(this.getBaseParams());
    }

    identifyUser() {
        if (this.isBooted) {
            this.updateClient(this.getUserParams());
        } else {
            this.bootClient(this.getUserParams());
        }
    }

    updateUser() {
        if (this.isTrackingActive && this.isBooted) {
            if (this.storeGetters['user/isGuest']) {
                this.updateClient(this.getBaseParams());
            } else {
                this.updateClient(this.getUserParams());
            }
        }
    }

    bootClient(data) {
        if (!this.isBooted) {
            this.client('boot', data);
            this.isBooted = true;
        }
    }

    updateClient(data) {
        if (data) {
            this.client('update', data);
        }
    }

    logout() {
        if (this.isTrackingActive) {
            this.client('shutdown');
            this.isBooted = false;
            // restart intercom after 1 second
            setTimeout(() => this.initialize(this.storeGetters), 1000);
        }
    }
}

export default new IntercomManager();
